import React, { useEffect, useState } from "react";

import { DashboardIcon } from "../../components/Icons";

const DISMISS_KEY_PREFIX = "ai_dashboard_trial_banner_dismissed:";

interface WebDashboardTrialStartBannerProps {
  /** Stable per-user key so the once-shown banner stays hidden across the user's dashboards. */
  dismissKey: string;
  onLearnMore: () => void;
  /** Whether to surface the "Learn More" (Contact Sales) link. Only paid users see it. */
  showLearnMore: boolean;
}

/**
 * Light-blue announcement banner shown to a dashboard owner during the early part of their AI
 * dashboard trial. Shown only once per user: it is marked seen in localStorage the moment it
 * renders, so it never reappears whether or not the user explicitly dismisses it.
 */
export const WebDashboardTrialStartBanner: React.FC<WebDashboardTrialStartBannerProps> = ({
  dismissKey,
  onLearnMore,
  showLearnMore,
}) => {
  const storageKey = `${DISMISS_KEY_PREFIX}${dismissKey}`;
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(storageKey) === "1";
    } catch {
      return false;
    }
  });

  // Persist "seen" as soon as the banner renders so it only ever shows once - even if the user
  // navigates away without clicking dismiss. The current session keeps showing it until dismissed.
  useEffect(() => {
    if (dismissed) {
      return;
    }
    try {
      localStorage.setItem(storageKey, "1");
    } catch {
      // Ignore storage failures - the banner just won't persist its seen state.
    }
  }, [dismissed, storageKey]);

  if (dismissed) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
  };

  // max-sm: match the mobile dashboard frame's content inset — 12px sides, 8px from the frame top,
  // which this banner sets when it is the frame's first child (otherwise #mainHeader does).
  return (
    <div className="flex justify-center px-6 pt-1 max-sm:px-[12px] max-sm:pt-[8px]">
      <div className="inline-flex items-center gap-10 rounded-lg bg-[#dee9fe] py-2.5 pl-2.5 pr-5">
        <div className="flex items-center gap-2.5 text-[13px] leading-[18px] text-[#111215]">
          <DashboardIcon withBackground={false} width={24} height={24} />
          <span className="font-semibold">You can trial AI Dashboards free for 30 days</span>
          {showLearnMore && (
            <button
              onClick={onLearnMore}
              className="font-semibold text-[#3053ff] underline"
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
            >
              Learn More
            </button>
          )}
        </div>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="flex h-4 w-4 items-center justify-center text-[#727272] hover:text-[#111215]"
          style={{ background: "none", border: "none", cursor: "pointer" }}
        >
          <span className="material-icons !text-[16px]">close</span>
        </button>
      </div>
    </div>
  );
};
